import coach_light from '@assets/svg/lineup/coaches_light.svg';
import coach_dark from '@assets/svg/lineup/coaches_dark.svg';
import { useTheme } from "@stores/ThemeContext";
import { T } from "@utils/enums";
import { useAuthenticatedImage } from '@utils/hooks/useAuthenticatedImage';
import { useScreenWidth } from '@utils/hooks/useScreenWidth';
import defaultFlagLight from '@assets/svg/lineup/coaches_flags/int_light.svg';
import defaultFlagDark from '@assets/svg/lineup/coaches_flags/int_dark.svg';
import yellowCardLight from '@assets/svg/lineup/yellow_card_light.svg';
import yellowCardDark from '@assets/svg/lineup/yellow_card_dark.svg';
import secondYellowCardLight from '@assets/svg/lineup/second_yellow_card_light.svg';
import secondYellowCardDark from '@assets/svg/statsConsoleAndOverview/matchEvents/second_yellow_card_dark.svg';
import redCardLight from '@assets/svg/lineup/red_card_light.svg';
import redCardDark from '@assets/svg/lineup/red_card_dark.svg';


interface CoachCardV2Props {
    coachName: string;
    teamName?: string | null;
    dataTestId: string;
    countryCode?: string;
    teamSide: "home" | "away";
    countryIcon?: string;
    isTemplate?: boolean;
    card?: "yellow" | "second_yellow" | "red";
}

const CoachCardV2 = ({ coachName, teamName, dataTestId, countryCode, teamSide, countryIcon, isTemplate = false, card }: CoachCardV2Props) => {
    const { theme } = useTheme(); 
    const { imageUrl: flagUrl } = useAuthenticatedImage(countryIcon);
    const screenWidth = useScreenWidth();
    const maxTextWidth = Math.floor(screenWidth * 0.4);

    const getCardIcon = () => {
        if (!card) return null;
        
        const isLight = theme === T.LIGHT;
        
        switch (card) {
            case "yellow":
                return isLight ? yellowCardLight : yellowCardDark;
            case "second_yellow":
                return isLight ? secondYellowCardLight : secondYellowCardDark;
            case "red":
                return isLight ? redCardLight : redCardDark;
            default:
                return null;
        }
    };

    const cardIcon = getCardIcon();

    return (
        <div data-testid={dataTestId} className={`w-1/2 flex flex-col
            font-medium text-[12px] leading-[16px] text-monoX dark:text-monoY mb-[4px] pt-[8px] 
            ${teamSide === 'home' ? 'ml-[20px] items-start' : 'mr-[20px] items-end'}`}>
            <div className="w-fit flex flex-col items-center">
            <div className="relative ">
                <img 
                    className={`w-[31px] h-[31px]`} 
                    src={theme === T.LIGHT ? coach_light : coach_dark} 
                    alt={`${coachName} coach`} 
                />
                {cardIcon && (
                    <div className="absolute -top-[4px] -left-[6px] w-auto h-[12px]
                     overflow-visible flex justify-center items-center">
                        <img 
                            className="w-full h-full object-cover"
                            src={cardIcon}
                            alt={`${card} card`}
                        />
                    </div>
                )}
                <div className="absolute -top-[8px] -right-[14px] w-[20px] h-[20px] rounded-full border-[1px] 
                    border-[#CCCCCC] overflow-visible">
                    <img 
                        className="w-full h-full object-cover"
                        src={ flagUrl ? flagUrl : (theme === T.LIGHT ? defaultFlagLight : defaultFlagDark) }
                        alt={`${countryCode} flag`}
                        onError={(e) => {
                            const fallbackSrc = theme === T.LIGHT ? defaultFlagLight : defaultFlagDark;
                            const isAlreadyFallback = e.currentTarget.src.includes('int_light') || e.currentTarget.src.includes('int_dark');
                            
                            if (!isAlreadyFallback) {
                                e.currentTarget.src = fallbackSrc;
                            } else {
                                e.currentTarget.style.display = 'none';
                            }
                        }}
                    />
                </div>
            </div>
            <div 
                className={`w-full text-center font-normal mt-[5px] truncate overflow-hidden px-2`}
                style={{ maxWidth: `${maxTextWidth}px` }}
            >
                {isTemplate ? `Coach ${teamName ?? ''}` : coachName}
            </div>
            </div>
        </div>
    );
};

export default CoachCardV2;